import React from "react";
import "./index.css";
import { CustomMenu, QuantityMap } from "../../../../../redux/allocationService";

interface ProteinPanelProps {
  menu: CustomMenu;
  packTotal: number;
  quantities: QuantityMap;
  onQuantityChange: (key: string, value: number) => void;
}

export const ProteinPanel: React.FC<ProteinPanelProps> = ({ 
  menu, 
  packTotal, 
  quantities, 
  onQuantityChange 
}) => {
  const assignedTotal = menu.sections.reduce((sTotal, section) => {
    return (
      sTotal +
      section.menuItems.reduce((iTotal, item) => {
        const key = `prot:${menu.itemId}:${item.itemId}`;
        return iTotal + (quantities[key] ?? 0);
      }, 0)
    );
  }, 0);

  const isComplete = packTotal > 0 && assignedTotal === packTotal;
  const isOver = assignedTotal > packTotal;
  const isUnder = packTotal > 0 && assignedTotal < packTotal;

  return (
    <div className="packs-protein-panel">
      <div className="packs-protein-summary">
        <span className="packs-protein-summary__label">Proteins assigned:</span>
        <span
          className={`packs-protein-summary__count ${
            isComplete
              ? "packs-protein-summary__count--complete"
              : isOver
              ? "packs-protein-summary__count--over"
              : isUnder
              ? "packs-protein-summary__count--under"
              : ""
          }`}
        >
          {assignedTotal} / {packTotal}
        </span>
        {isComplete && (
          <span className="packs-protein-summary__status packs-protein-summary__status--complete">
            ✓ Complete
          </span>
        )}
        {isOver && (
          <span className="packs-protein-summary__status packs-protein-summary__status--over">
            +{assignedTotal - packTotal} Over
          </span>
        )}
        {isUnder && (
          <span className="packs-protein-summary__status packs-protein-summary__status--under">
            -{packTotal - assignedTotal} Short
          </span>
        )}
      </div>

      {menu.sections.length === 0 && (
        <p className="packs-protein-empty">No sections defined for this menu.</p>
      )}

      <div className="packs-protein-sections-grid">
        {menu.sections.map((section, sIdx) => {
          const sectionTotal = section.menuItems.reduce((t, item) => {
            const key = `prot:${menu.itemId}:${item.itemId}`;
            return t + (quantities[key] ?? 0);
          }, 0);

          return (
            <div key={sIdx} className="packs-protein-section">
              <div className="packs-protein-section__header">
                <span className="packs-protein-section__name">
                  {section.displayName}
                </span>
                {sectionTotal > 0 && (
                  <span className="packs-protein-section__count">
                    {sectionTotal}
                  </span>
                )}
              </div>

              {section.menuItems.length === 0 ? (
                <p className="packs-protein-section__empty">No items</p>
              ) : (
                <div className="packs-protein-items">
                  {section.menuItems.map((item) => {
                    const key = `prot:${menu.itemId}:${item.itemId}`;
                    const qty = quantities[key] ?? 0;
                    return (
                      <div
                        key={item.itemId}
                        className={`packs-protein-item ${
                          qty > 0 ? "packs-protein-item--active" : ""
                        }`}
                      >
                        <div className="packs-protein-item__info">
                          <span className="packs-protein-item__name" title={item.displayName}>
                            {item.displayName}
                          </span>
                          <span className="packs-protein-item__sku">
                            {item.skuId || "No SKU"}
                          </span>
                        </div>
                        <input
                          className="packs-qty-input packs-qty-input--sm"
                          type="number"
                          min={0}
                          placeholder="0"
                          value={qty === 0 ? "" : qty}
                          onChange={(e) =>
                            onQuantityChange(
                              key,
                              Math.max(0, parseInt(e.target.value) || 0)
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};